import type { OMSConfig, StrategyEvaluator, SignalResult, StrategyContext } from '../types';
import { ensureFlatOpts } from '../../analysis/trend-evaluator';
import { extractSnapshotOptions } from '../../../lib/snapshot-search';

/**
 * StrikeRangeShift — Strike Activity Signal (All History)
 *
 * BULLISH when:
 *   - Strikes Dropped PE Side == Lower strikes
 *   - Strikes Entered CE Side == Higher strikes
 *   - Strikes Dropped CE Side == Lower strikes
 *   - Strikes Entered PE Side == Higher strikes
 *
 * BEARISH when:
 *   - Strikes Dropped PE Side == Higher strikes
 *   - Strikes Entered CE Side == Lower strikes
 *   - Strikes Dropped CE Side == Higher strikes
 *   - Strikes Entered PE Side == Lower strikes
 *
 * "Lower" / "Higher" is determined by comparing average strike vs ATM.
 */
export const evaluateStrikeRangeShift: StrategyEvaluator = (data: any, currentUnixTime: number, config: OMSConfig, context: StrategyContext): SignalResult | null => {
    if (!data) return null;

    const { symbol, expiry, activeRange, gap, settingsMode, baseSnapshot } = context;
    if (!baseSnapshot) return null;

    const spot = data.spotPrice || data.index_ltp || data.ltp || 0;
    if (spot <= 0) return null;
    
    const trendAnalysisResult = context.liveIndicators?.trendAnalysisData;
    const fullTrendData = Array.isArray(trendAnalysisResult) ? trendAnalysisResult : (trendAnalysisResult?.data || []);
    if (fullTrendData.length < 2) return null;

    const topN = 10;

    const cellDataKeys: Record<string, string> = {
        'Combined OI': 'combOI',
        'Combined Volume': 'combVol',
        'Top OI': 'oi',
        'Top Volume': 'volume',
        'Top Chng OI': 'oiChg',
    };
    const valKey = cellDataKeys[settingsMode] || 'oi';

    // ─── Apply Fixed Anchor to Prevent Sliding Window Whipsaws ───
    const liveSpotPrice = fullTrendData[0]?.spotPrice || fullTrendData[0]?.indexLtp || spot;
    const fixedAtmStrike = Math.round(liveSpotPrice / gap) * gap;
    const fixedMinStrike = fixedAtmStrike - (activeRange * gap);
    const fixedMaxStrike = fixedAtmStrike + (activeRange * gap);

    // ─── Recalculate ITM strikes for each row using the Fixed Anchor ───
    const processedData = fullTrendData.map((row: any) => {
        const rawOpts = row.flatOpts || row.rawOpts || [];
        
        if (rawOpts.length === 0) {
            return { ...row, itmStrikesCalculated: [] };
        }
        
        // 1. Filter by the identical locked window for ALL rows
        const inRangeRow = rawOpts.filter((opt: any) => opt.strike >= fixedMinStrike && opt.strike <= fixedMaxStrike);
        
        // 2. Determine ITM status using the fixed boundary, so spot price wiggles don't cause fake "Drops"
        const itmOpts = inRangeRow.filter((opt: any) => {
            if (opt.strike === fixedAtmStrike) return true;
            const isCE = opt.type === 'CE';
            return isCE ? opt.strike < fixedAtmStrike : opt.strike > fixedAtmStrike;
        });
        
        const uniqueItmOpts: any[] = [];
        const seen = new Set<string>();
        for (const opt of itmOpts) {
            const key = `${opt.strike}_${opt.type}`;
            if (!seen.has(key)) {
                seen.add(key);
                uniqueItmOpts.push(opt);
            }
        }
        
        uniqueItmOpts.sort((a, b) => (Number(b[valKey]) || 0) - (Number(a[valKey]) || 0));
        const topItmOpts = uniqueItmOpts.slice(0, topN);
        return { ...row, itmStrikesCalculated: topItmOpts };
    });

    // ─── Compare Live vs Historical ───
    const liveRowCalculated = processedData[0];
    if (!liveRowCalculated || !liveRowCalculated.itmStrikesCalculated || liveRowCalculated.itmStrikesCalculated.length === 0) {
        return null;
    }

    const liveITM = liveRowCalculated.itmStrikesCalculated;
    const liveCE = new Set<number>(liveITM.filter((o: any) => o && o.type === 'CE').map((o: any) => o.strike as number));
    const livePE = new Set<number>(liveITM.filter((o: any) => o && o.type === 'PE').map((o: any) => o.strike as number));

    const allHistCE = new Set<number>();
    const allHistPE = new Set<number>();
    
    processedData.slice(1).forEach((row: any) => {
        const itm = row.itmStrikesCalculated;
        if (!itm) return;
        itm.forEach((o: any) => {
            if (!o) return;
            if (o.type === 'CE') allHistCE.add(o.strike);
            else if (o.type === 'PE') allHistPE.add(o.strike);
        });
    });

    // Valid candidates in current range
    const liveRawOptsArr = liveRowCalculated.flatOpts || liveRowCalculated.rawOpts || context.chain || extractSnapshotOptions(data, symbol, expiry) || [];
    const liveFlatOpts = ensureFlatOpts(liveRawOptsArr);

    const spotPrice = liveSpotPrice;
    const atmStrike = fixedAtmStrike;
    const minStrike = fixedMinStrike;
    const maxStrike = fixedMaxStrike;

    const inRange = liveFlatOpts.filter((opt: any) => opt.strike >= minStrike && opt.strike <= maxStrike);
    const validCE = new Set(inRange.filter((o: any) => o.type === 'CE').map((o: any) => o.strike));
    const validPE = new Set(inRange.filter((o: any) => o.type === 'PE').map((o: any) => o.strike));

    // Missing: was in historical but NOT in live (and still valid in range)
    const mCE = Array.from(allHistCE).filter(s => !liveCE.has(s) && validCE.has(s)).sort((a, b) => a - b);
    const mPE = Array.from(allHistPE).filter(s => !livePE.has(s) && validPE.has(s)).sort((a, b) => a - b);

    const ceMissCount = new Map<number, number>();
    mCE.forEach(strike => {
        let missed = 0;
        for (let i = 0; i < processedData.length; i++) {
            if (processedData[i].itmStrikesCalculated.some((o: any) => o && o.type === 'CE' && o.strike === strike)) {
                missed = i;
                break;
            }
        }
        ceMissCount.set(strike, missed);
    });

    const peMissCount = new Map<number, number>();
    mPE.forEach(strike => {
        let missed = 0;
        for (let i = 0; i < processedData.length; i++) {
            if (processedData[i].itmStrikesCalculated.some((o: any) => o && o.type === 'PE' && o.strike === strike)) {
                missed = i;
                break;
            }
        }
        peMissCount.set(strike, missed);
    });

    // New/Enter: in live, track consecutive availability from index 0
    const ceEnterCount = new Map<number, number>();
    const peEnterCount = new Map<number, number>();
    
    Array.from(liveCE).forEach(strike => {
        let available = 0;
        for (let i = 0; i < processedData.length; i++) {
            if (processedData[i].itmStrikesCalculated.some((o: any) => o && o.type === 'CE' && o.strike === strike)) {
                available++;
            } else {
                break;
            }
        }
        ceEnterCount.set(strike, available);
    });

    Array.from(livePE).forEach(strike => {
        let available = 0;
        for (let i = 0; i < processedData.length; i++) {
            if (processedData[i].itmStrikesCalculated.some((o: any) => o && o.type === 'PE' && o.strike === strike)) {
                available++;
            } else {
                break;
            }
        }
        peEnterCount.set(strike, available);
    });

    const nCE = Array.from(liveCE).filter(strike => {
        const count = ceEnterCount.get(strike) || 0;
        return count > 0 && count < processedData.length;
    }).sort((a, b) => a - b);
    
    const nPE = Array.from(livePE).filter(strike => {
        const count = peEnterCount.get(strike) || 0;
        return count > 0 && count < processedData.length;
    }).sort((a, b) => a - b);

    // ─── Determine BULL/BEAR — Synced exactly with StrikesActivityDashboard.tsx ───
    const allMissing = [...mCE, ...mPE];
    const allNew = [...nCE, ...nPE];

    // Guard: require BOTH missing AND new strikes to exist (same as Dashboard)
    if (allMissing.length === 0 || allNew.length === 0) return null;

    const avgMissCE = mCE.length > 0 ? mCE.reduce((a, b) => a + b, 0) / mCE.length : 0;
    const avgNewCE = nCE.length > 0 ? nCE.reduce((a, b) => a + b, 0) / nCE.length : 0;
    
    const avgMissPE = mPE.length > 0 ? mPE.reduce((a, b) => a + b, 0) / mPE.length : 0;
    const avgNewPE = nPE.length > 0 ? nPE.reduce((a, b) => a + b, 0) / nPE.length : 0;
    
    const ceShiftUp = mCE.length > 0 && nCE.length > 0 && avgNewCE > avgMissCE;
    const peShiftUp = mPE.length > 0 && nPE.length > 0 && avgNewPE > avgMissPE;
    
    const ceShiftDown = mCE.length > 0 && nCE.length > 0 && avgNewCE < avgMissCE;
    const peShiftDown = mPE.length > 0 && nPE.length > 0 && avgNewPE < avgMissPE;
    
    let status = 'Sideways';
    if (ceShiftUp || peShiftUp) {
        status = 'BULL';
    } else if (ceShiftDown || peShiftDown) {
        status = 'BEAR';
    }

    if (status === 'Sideways') return null;

    // ─── Entry Strike (ATM option) ───
    let entryPrice = 0;
    const optionType = status === 'BULL' ? 'CE' : 'PE';
    const atmRow = liveFlatOpts.find(r => r.strike === atmStrike && r.type === optionType);
    if (atmRow) {
        entryPrice = atmRow.ltp || atmRow.close || atmRow.price || 0;
    }
    
    if (entryPrice <= 0) return null;

    return {
        trade_type: status as any,
        strike: atmStrike,
        option_type: optionType,
        entry_price: entryPrice,
        remarks: `${status} Activity (Drop CE:${mCE.join(',')||'-'} PE:${mPE.join(',')||'-'} | New CE:${nCE.join(',')||'-'} PE:${nPE.join(',')||'-'} | CEup:${ceShiftUp} PEup:${peShiftUp} CEdn:${ceShiftDown} PEdn:${peShiftDown})`
    };
};
